import styled from '@emotion/styled';
import RefreshIcon from '@mui/icons-material/Refresh';
import { CircularProgress } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import routes from '../../config/routes';
import { hasPermissions } from '../../helpers';
import {
  accountingEntriesFilterAccountingMonthSelector,
  accountingEntriesFilterCountrySelector,
  accountingEntriesCompanyCodesLoadingSelector,
  accountingEntriesFindRequested,
  userPermissionsDataSelector,
} from '../../state';
import { AppPermissions } from '../../types';
import { LoadingButton } from '../buttons';
import { AccountingEntriesFilters } from './AccountingEntriesFilters';

const ActionContainer = styled.div`
  display: flex;
  width: max-content;
  margin-left: auto;
  justify-content: right;

  > button:first-of-type {
    margin-right: ${props => props.theme.spacing(2)};
  }

  ${props => props.theme.breakpoints.down('md')} {
    margin-left: auto;
    margin-right: auto;
    justify-content: center;
  }
`;

interface ButtonApplyProps {
  applyDisabled: boolean;
}

const ButtonApply = ({ applyDisabled }: ButtonApplyProps) => {
  const dispatch = useDispatch();
  const handleClickApply = useCallback(
    () => dispatch(accountingEntriesFindRequested()),
    [dispatch]
  );
  const { t } = useTranslation();
  const loading = useSelector(accountingEntriesCompanyCodesLoadingSelector);
  return (
    <LoadingButton
      color="primary"
      variant="contained"
      onClick={handleClickApply}
      loading={loading}
      disabled={loading || applyDisabled}
    >
      {t('navigation.buttons.apply')}
    </LoadingButton>
  );
};

const ButtonRefresh = () => {
  const dispatch = useDispatch();
  const handleClickApply = useCallback(
    () => dispatch(accountingEntriesFindRequested()),
    [dispatch]
  );
  const { t } = useTranslation();
  const loading = useSelector(accountingEntriesCompanyCodesLoadingSelector);
  return (
    <IconButton
      aria-label={t('navigation.buttons.refresh')}
      size="small"
      onClick={handleClickApply}
      disabled={loading}
      sx={theme => ({
        color: theme.palette.mode === 'dark' ? 'secondary.main' : undefined,
      })}
    >
      {!loading && <RefreshIcon color="inherit" />}
      {loading && <CircularProgress size={15} />}
    </IconButton>
  );
};

const CreateAccountingEntries = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const path = `${routes.protectedNestedRoutes.accountingEntries.children.create.path}`;
  const handleClick = useCallback(() => navigate(path), [navigate, path]);

  return (
    <Button color="primary" variant="contained" onClick={handleClick}>
      {t('accountingEntries.labels.createAccountingEntries')}
    </Button>
  );
};

const AccountingEntriesSearch = () => {
  const userPermissions = useSelector(userPermissionsDataSelector).map(i => i.permission);
  const hasCountry = Boolean(useSelector(accountingEntriesFilterCountrySelector));
  const accountingMonth = useSelector(accountingEntriesFilterAccountingMonthSelector);
  const applyDisabled = !(hasCountry && accountingMonth.accept);
  const countryRequiredIncomplete = applyDisabled && !hasCountry;
  const accountingMonthRequiredIncomplete = applyDisabled && !accountingMonth.accept;

  const actions = (
    <ActionContainer data-tour="process-actions">
      <ButtonRefresh />
      {hasPermissions(userPermissions, [
        AppPermissions.ACCOUNTING_ENTRIES_COMPLETE,
        AppPermissions.ACCOUNTING_ENTRIES_EXECUTION,
      ]) && <CreateAccountingEntries />}
      <ButtonApply applyDisabled={applyDisabled} />
    </ActionContainer>
  );
  return (
    <AccountingEntriesFilters
      actions={actions}
      applyDisabled={applyDisabled}
      countryRequiredIncomplete={countryRequiredIncomplete}
      accountingMonthRequiredIncomplete={accountingMonthRequiredIncomplete}
    />
  );
};

export default AccountingEntriesSearch;
